import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu as List, X, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useM } from "./useM";

const LINKS = [
  { to: "/features", key: "nav.features", id: "features" },
  { to: "/how-it-works", key: "nav.how", id: "how-it-works" },
  { to: "/about", key: "nav.about", id: "about" },
];

const AuthActions = ({ m, user, mobile }) =>
  user ? (
    <Link to="/dashboard" className="inline-flex items-center gap-2 rounded-full bg-mk-yellow px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white" data-testid={mobile ? "mobile-nav-dashboard" : "nav-link-dashboard"}>
      {m("nav.dashboard")} <ArrowRight size={16} />
    </Link>
  ) : (
    <>
      <Link to="/login" className="px-3 py-2 text-sm font-medium text-white/80 transition-colors hover:text-white" data-testid={mobile ? "mobile-nav-login" : "nav-link-login"}>{m("nav.login")}</Link>
      <Link to="/register" className="rounded-full bg-mk-yellow px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white" data-testid={mobile ? "mobile-nav-get-started" : "nav-link-get-started"}>{m("nav.start")}</Link>
    </>
  );

export const Navbar = () => {
  const m = useM();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);

  const solid = scrolled || open;
  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${solid ? "border-b border-white/10 bg-mk-bg/80 backdrop-blur-xl" : "border-b border-transparent"}`} data-testid="public-navbar">
      <nav className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        {pathname === "/" ? (
          <div className="flex cursor-default select-none items-center gap-2.5 font-heading text-2xl font-extrabold tracking-tight text-white" data-testid="nav-brand">
            <Logo size={26} className="text-mk-yellow" /> KIHAP
          </div>
        ) : (
          <Link to="/" className="flex items-center gap-2.5 font-heading text-2xl font-extrabold tracking-tight text-white" data-testid="nav-link-home">
            <Logo size={26} className="text-mk-yellow" /> KIHAP
          </Link>
        )}
        <div className="hidden items-center gap-1 md:flex">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.id}`}
              className={({ isActive }) => `rounded-full px-4 py-2 text-sm font-medium transition-colors ${isActive ? "text-mk-yellow" : "text-white/70 hover:text-white"}`}>
              {m(l.key)}
            </NavLink>
          ))}
        </div>
        <div className="hidden items-center gap-2 md:flex">
          <LanguageSwitcher className="!border-white/15 !bg-transparent" />
          <AuthActions m={m} user={user} />
        </div>
        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher className="!border-white/15 !bg-transparent" />
          <button onClick={() => setOpen(!open)} className="rounded-full p-2 text-white" aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </nav>
      {open && (
        <div className="border-t border-white/10 px-6 pb-8 pt-4 md:hidden" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="block border-b border-white/5 py-4 font-heading text-2xl font-bold uppercase text-white" data-testid={`mobile-nav-${l.id}`}>{m(l.key)}</Link>
          ))}
          <div className="mt-6 flex items-center gap-3"><AuthActions m={m} user={user} mobile /></div>
        </div>
      )}
    </header>
  );
};
